import '../css/FloatingControls.scss'
import { sendData } from '../data/sendData'
import { Button } from '@material-ui/core'
import { blue, grey } from '@material-ui/core/colors'
import { ThemeProvider, createTheme } from '@material-ui/core/styles'
import { BackspaceRounded, MicRounded } from '@material-ui/icons'
import 'animate.css'
import PropTypes from 'prop-types'
import React, { Component, createRef } from 'react'
import isEqual from 'react-fast-compare'

const theme = createTheme({
  palette: {
    primary: {
      main: blue[500],
      darker: blue[800],
      contrastText: blue[50],
    },
    secondary: {
      main: grey[500],
      darker: grey[800],
      contrastText: grey[50],
    },
  },
})

export default class BaselineFloatingControls extends Component {
  constructor() {
    super()
    this.controlsRef = createRef()
    this.replaceRef = createRef()
    this.deleteRef = createRef()

    this.replaceStart = this._handleReplaceStart.bind(this)
    this.replaceEnd = this._handleReplaceEnd.bind(this)
    this.delete = this._handleDelete.bind(this)
  }

  shouldComponentUpdate(prevProps, prevState) {
    return !isEqual(prevProps, this.props) || !isEqual(prevState, this.state)
  }

  shouldShowControls() {
    return getTextareaSelection().length !== 0 && !this.props.inSelecting
  }

  buttonClassNames(show) {
    return {
      controls: 'floating-controls' + (show ? ' show' : ' no-show'),
      replace:
        'controls-button replace-button animate__animated' +
        (show ? ' animate__slideInUp' : ' animate__slideOutDown'),
      delete:
        'controls-button delete-button animate__animated' +
        (show ? ' animate__slideInUp' : ' animate__slideOutDown'),
    }
  }

  /* -------------------------------------------------------------------------- */
  /*                             Text Replace Start                             */
  /* -------------------------------------------------------------------------- */

  _handleReplaceStart(e) {
    e.stopPropagation()
    // initRecording(this.handleTempResult.bind(this))

    const tArea = document.querySelector('#baseline-text')
    tArea.focus()

    sendData('speechstart', e, {
      touchSpeed: 0,
      touchTarget: 'speechButton',
      selectionArray: getTextareaSelection(),
      textArray: getTextareaText(),
    })

    return false
  }

  handleTempResult(r) {
    // this.props.blockChangeText(this.updateTextArray(r))
    // if (r.length && divideText(r).length) this.props.onStartRecognition(r)
    // TODO
  }

  /* -------------------------------------------------------------------------- */
  /*                              Text Replace End                              */
  /* -------------------------------------------------------------------------- */

  _handleReplaceEnd(e) {
    // stopRecording(this.handleFinalResult.bind(this))

    const tArea = document.querySelector('#baseline-text')
    tArea.focus()

    return false
  }

  handleFinalResult(r) {
    const newArray = this.updateTextArray(r)

    sendData('speechend', null, {
      touchSpeed: 0,
      touchTarget: 'speechButton',
      selectionArray: getTextareaSelection(),
      textArray: newArray,
    })

    this.props.bodyChangeText(newArray)
  }

  updateTextArray(r) {
    const { text } = this.props

    const tArea = document.querySelector('#baseline-text')
    tArea.focus()

    let start = tArea.selectionStart
    let end = tArea.selectionEnd

    if (start + 1 === end) {
      if (
        text[start - 2] &&
        text[start - 2] !== '.' &&
        text[start - 2] !== '!' &&
        text[start - 2] !== '?'
      ) {
        if (
          text[end - 1] === '.' ||
          text[end - 1] === '!' ||
          text[end - 1] === '?'
        ) {
          return (
            text.slice(0, start) +
            ' ' +
            r[0].toLowerCase() +
            r.slice(1) +
            ' ' +
            text.slice(end)
          )
        } else {
          return (
            text.slice(0, start) +
            ' ' +
            r[0].toLowerCase() +
            r.slice(1, r.length - 1) +
            ' ' +
            text.slice(end)
          )
        }
      } else if (
        text[end - 1] === '.' ||
        text[end - 1] === '!' ||
        text[end - 1] === '?'
      ) {
        return text.slice(0, start) + ' ' + r + ' ' + text.slice(end)
      }
    } else {
      if (
        text[start - 2] &&
        text[start - 2] !== '.' &&
        text[start - 2] !== '!' &&
        text[start - 2] !== '?'
      ) {
        if (
          text[end - 1] === '.' ||
          text[end - 1] === '!' ||
          text[end - 1] === '?'
        ) {
          return (
            text.slice(0, start) +
            r[0].toLowerCase() +
            r.slice(1) +
            text.slice(end)
          )
        } else {
          return (
            text.slice(0, start) +
            r[0].toLowerCase() +
            r.slice(1, r.length - 2) +
            text.slice(end)
          )
        }
      } else if (
        text[end - 1] === '.' ||
        text[end - 1] === '!' ||
        text[end - 1] === '?'
      ) {
        return text.slice(0, start) + r + text.slice(end)
      }
    }

    return text.slice(0, start) + r.slice(0, r.length - 2) + text.slice(end)
  }

  /* -------------------------------------------------------------------------- */
  /*                                   Delete                                   */
  /* -------------------------------------------------------------------------- */

  _handleDelete(e) {
    e.preventDefault()

    const tArea = document.querySelector('#baseline-text')
    tArea.focus()

    const { text, bodyChangeText } = this.props

    let start = tArea.selectionStart
    let end = tArea.selectionEnd

    const selection = getTextareaSelection()
    const newText = text.slice(0, start) + text.slice(end)

    bodyChangeText(newText)

    sendData('deleteblock', e, {
      touchSpeed: 0,
      touchTarget: 'deleteButton',
      selectionArray: selection,
      textArray: newText,
    })

    return false
  }

  render() {
    const show = this.shouldShowControls()
    const classes = this.buttonClassNames(show)

    return (
      <ThemeProvider theme={theme}>
        <div className={classes.controls} ref={this.controlsRef}>
          <Button
            key="replace-button"
            ref={this.replaceRef}
            className={classes.replace}
            variant="contained"
            startIcon={
              <MicRounded
                sx={{
                  fill: blue[50],
                }}
              />
            }
            size="large"
            disableElevation
            onTouchStart={this.replaceStart}
            onTouchEnd={this.replaceEnd}
            onContextMenu={e => {
              e.preventDefault()
            }}
          >
            Replace
          </Button>
          <Button
            key="delete-button"
            ref={this.deleteRef}
            className={classes.delete}
            variant="contained"
            color="secondary"
            size="large"
            disableElevation
            onClick={this.delete}
          >
            <BackspaceRounded
              sx={{
                fill: grey[50],
              }}
            />
          </Button>
          {/* <IconButton
            key="delete-button"
            ref={this.delete}
            className={classes.delete}
            aria-label="backspace"
            size="large"
            sx={{
              position: 'fixed',
              bottom: '1rem',
              right: '1rem',
            }}
          >
            <Delete
              sx={{
                fill: grey[400],
              }}
            />
          </IconButton> */}
        </div>
      </ThemeProvider>
    )
  }
}

BaselineFloatingControls.propTypes = {
  text: PropTypes.string,
  inSelecting: PropTypes.bool,
  bodyChangeText: PropTypes.func,
  onStartRecognition: PropTypes.func,
  onEndRecognition: PropTypes.func,
}

/* -------------------------------------------------------------------------- */

const getTextareaSelection = () => {
  const tA = document.querySelector('#baseline-text')
  if (!tA) return ''
  return tA.value.substring(tA.selectionStart, tA.selectionEnd)
}

const getTextareaText = () => {
  const tA = document.querySelector('#baseline-text')
  if (!tA) return ''
  return tA.value
}
